import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import usePageTitle from 'hooks/usePageTitle';
import useDecode from "../../../hooks/useDecode";
import PageHeader from "../../blocks/PageHeader/PageHeader";
import LoaderMin from "components/blocks/Loader/LoaderMin";
import Icon from "../../blocks/Icon/Icon";
import CommBubble from "../../blocks/CommBubble/CommBubble";
import useGetUserChatCommunicationByIdRecord from "../../../services/communications/useGetUserChatCommunicationByIdRecord";
import useCreateUserCommunicationRecord from "services/communications/useCreateUserCommunicationRecord";
import useCheckTypes from "hooks/useCheckRecordStatusAndTypes";
import useGetRole from "hooks/useGetRole";
import "./CommunicationChatGeneral.scss";

interface IdataComunication {
  externalUserId: string;
  userId: string;
  prestacionId: string;
  asunto: string;
  mensaje: string;
  files: any[];
  comunicacionReferenciaId: string | null;
}

const formatDateEs = (dateStr: string): string => {
  const d = new Date(dateStr);
  const weekday = d.toLocaleDateString("es-ES", { weekday: "long" });
  const day = d.getDate();
  const month = d.toLocaleDateString("es-ES", { month: "long" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${day} de ${month} de ${year} · ${hh}:${mm}h`;
};

const CommunicationChat: React.FC = () => {
  usePageTitle('Chat');
  const navigate    = useNavigate();
  const location    = useLocation();
  const { id }      = useParams<{ id: string }>();
  const arrParamsId   = id?.split("_") || [];
  const recordType    = arrParamsId[0] || "";
  const recordId      = arrParamsId[1] || "";
  const communicationId = arrParamsId[2] || "";

  const tokenResponse  = useDecode("token");
  const userId         = tokenResponse && tokenResponse.extension_id;
  const externalUserId = tokenResponse?.extension_external_id;
  const [userTokenId, setUserTokenId] = useState("");
  const { isImpersonation } = useGetRole;
  const isImpersonating = isImpersonation();

  const [showReply, setShowReply]   = useState(false);
  const [replyText, setReplyText]   = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const rfileRef = useRef<HTMLInputElement>(null);

  const {
    getUserChatCommunicationByIdRecord,
    userChatCommunicationRecord,
  } = useGetUserChatCommunicationByIdRecord();

  const {
    createUserCommunicationRecord,
    successCreateUserCommunicationRecord,
  } = useCreateUserCommunicationRecord();

  const { checkRecordType } = useCheckTypes();
  const recordTypeText = checkRecordType(parseInt(recordType));

  useEffect(() => {
    if (tokenResponse !== undefined) setUserTokenId(tokenResponse.extension_id);
    getUserChatCommunicationByIdRecord(userId, recordId);
  }, [successCreateUserCommunicationRecord]);

  const dataCommunicationChat =
    userChatCommunicationRecord &&
    userChatCommunicationRecord.find((c: any) => c.id === communicationId);

  const replies: any[] =
    dataCommunicationChat?.comunicaciones ||
    dataCommunicationChat?.respuestas ||
    [];

  const convertBase64 = async (files: File[]): Promise<any[]> => {
    if (!files || files.length === 0) return [];
    return Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              const parts = file.name.split(".");
              resolve({
                name: file.name,
                extension: parts.pop(),
                externalUri: "",
                base64: (reader.result as string).split(",")[1],
                externalId: "",
              });
            };
            reader.onerror = reject;
          })
      )
    );
  };

  const attach  = () => rfileRef.current?.click();
  const onPick  = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const captured = Array.from(e.target.files);
      e.target.value = "";
      setReplyFiles((prev) => [...prev, ...captured]);
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    const dataFiles = await convertBase64(replyFiles);
    const payload: IdataComunication = {
      externalUserId,
      userId,
      prestacionId: recordId,
      asunto: dataCommunicationChat?.asunto ?? "",
      mensaje: replyText,
      files: dataFiles,
      comunicacionReferenciaId: dataCommunicationChat?.puedeResponder
        ? dataCommunicationChat?.id
        : null,
    };
    createUserCommunicationRecord(userId, recordId, payload);
    setReplyText("");
    setReplyFiles([]);
    setShowReply(false);
  };

  const isLoading = !userChatCommunicationRecord;

  return (
    <>
      {isLoading ? (
        <section className="container">
          <LoaderMin />
        </section>
      ) : (
        <div className="container">
          <PageHeader
            title={dataCommunicationChat?.asunto || "Comunicación"}
            subtitle={
              dataCommunicationChat && (
                <>
                  Enviado por:{" "}
                  <strong>
                    {dataCommunicationChat.autorUsuario ? "Usted" : dataCommunicationChat.nombreAutor}
                  </strong>
                  {dataCommunicationChat.created && (
                    <><br />{formatDateEs(dataCommunicationChat.created)}</>
                  )}
                </>
              )
            }
            crumbs={[
              { label: "Inicio",    onClick: () => navigate("/inicio") },
              { label: "Historial", onClick: () => navigate("/historial") },
              { label: recordTypeText },
              { label: dataCommunicationChat?.asunto || "Comunicación" },
            ]}
            actions={
              <button className="btn btn--ghost btn--sm" onClick={() => navigate(-1)}>
                <Icon name="arrow-left" size={16} /> Volver
              </button>
            }
          />

          <div className="row">
            <div className="col">
              <div className="comm-content">
                {dataCommunicationChat && (
                  <>
                    <div className="comm-thread">
                      <CommBubble
                        msg={dataCommunicationChat}
                        kind="info"
                        isOriginal
                      />
                      {replies.map((r: any, i: number) => (
                        <CommBubble key={r.id || i} msg={r} kind="reply" />
                      ))}
                    </div>

                    {dataCommunicationChat.puedeResponder && !isImpersonating && (
                      !showReply ? (
                        <div className="comm-reply-cta">
                          <button className="btn btn--primary" onClick={() => setShowReply(true)}>
                            <Icon name="plus" size={16} /> Responder
                          </button>
                        </div>
                      ) : (
                        <form className="comm-reply" onSubmit={sendReply}>
                          <div className="comm-reply__head">
                            <Icon name="chat" size={18} />
                            <span>Responder a esta comunicación</span>
                          </div>
                          <textarea
                            className="field__input field__textarea"
                            placeholder="Escribe tu respuesta..."
                            rows={5}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            required
                          />
                          <input
                            ref={rfileRef}
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.zip,.7z,.txt,.csv,.odt,.ods,.webp"
                            style={{ display: "none" }}
                            onChange={onPick}
                          />
                          {replyFiles.length > 0 && (
                            <div className="comm-reply__files">
                              {replyFiles.map((f, i) => (
                                <div key={i} className="comm-reply__file">
                                  <Icon name="file" size={16} />
                                  <span className="comm-reply__file-name">{f.name}</span>
                                  <button
                                    type="button"
                                    className="comm-reply__file-x"
                                    aria-label="Quitar archivo"
                                    onClick={() => setReplyFiles((prev) => prev.filter((_, j) => j !== i))}
                                  >
                                    <Icon name="x" size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="comm-reply__actions">
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => { setShowReply(false); setReplyText(""); setReplyFiles([]); }}
                            >
                              Cancelar
                            </button>
                            <button type="button" className="btn btn--secondary btn--sm" onClick={attach}>
                              <Icon name="paperclip" size={16} /> Adjuntar
                            </button>
                            <button type="submit" className="btn btn--primary btn--sm">
                              Enviar respuesta <Icon name="arrow-right" size={16} />
                            </button>
                          </div>
                        </form>
                      )
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommunicationChat;
